/**
 * Work out which offering a question is about, by name.
 *
 * The assistant's SSE protocol carries `token`/`tool`/`done`/`error` and the
 * `tool` frame names the tool without its payload, so the browser never learns
 * which offering the agent looked up. Rather than widen the wire protocol, this
 * matches the user's own words against the catalogue the SPA already holds.
 *
 * It reads the **question**, not the answer. Almost every answer mentions
 * Panorama — it is the name of the platform the assistant lives in — so
 * matching on answers would offer a Panorama factsheet after nearly every turn.
 * A question naming Panorama really is a question about the Panorama offering,
 * which is the distinction that makes this usable.
 */

/** Anything with a display name; the catalogue summary and the full record both fit. */
export interface OfferingLike {
  displayName?: string | null;
}

/** Names shorter than this match too much ordinary prose to be evidence. */
const MIN_NAME_LENGTH = 2;

/**
 * The same, for linking names inside an answer.
 *
 * Stricter than the threshold above, and deliberately a separate number rather
 * than a change to it. Two-letter names — "AI", "BI" — turn up in the body of
 * almost every answer, and linking each one makes a paragraph about analytics
 * into a field of links to something nobody asked about. A *question* naming
 * "AI" is still good evidence, so raising the shared value would narrow the
 * factsheet action for no reason.
 */
const MIN_LINKED_NAME_LENGTH = 3;

/** A run of letters or digits either side means the name is part of a longer word. */
const BOUNDARY = '[^\\p{L}\\p{N}]';

/**
 * What may sit between two parts of a name, in either text.
 *
 * Zero or more, so it closes both gaps at once: the catalogue writes "Power BI"
 * and a reader types "PowerBI", or the catalogue writes "7Zip" and a reader
 * types "7 zip". Both were misses, and the second one is why this exists — the
 * factsheet action reads the question, so an answer could link "7Zip" while the
 * button for it stayed hidden because the question said "7-Zip".
 */
const SOFT_SEPARATOR = '[\\s._-]*';

/** A point inside a name where a reader might reasonably put a separator. */
const CLASS_CHANGE = [
  // lower → upper: PowerBI, iOS
  /(?<=\p{Ll})(?=\p{Lu})/gu,
  // letter → digit and back: Office365, 7Zip
  /(?<=\p{L})(?=\p{N})/gu,
  /(?<=\p{N})(?=\p{L})/gu,
];

const SPLIT = '\u0000';

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * The parts of a name that must appear in order, separators aside.
 *
 * Split on the separators the name already has AND on every change of
 * character class inside it. Deliberately not on every character: joining
 * single letters with an optional separator would make "7 z i p" a match for
 * "7Zip", and a name is not a sequence of initials.
 */
function partsOf(name: string): string[] {
  return name
    .split(/[\s._-]+/)
    .filter(Boolean)
    .flatMap((part) =>
      CLASS_CHANGE.reduce(
        (acc, at) => acc.replace(at, SPLIT),
        part,
      ).split(SPLIT),
    )
    .filter(Boolean);
}

function mentions(text: string, name: string): boolean {
  const parts = partsOf(name).map(escapeRegExp);
  if (!parts.length) {
    return false;
  }
  // The outer anchors are untouched: this loosens what counts as the *inside*
  // of a name, never whether the name has to stand as its own word. "7Zippers"
  // is still not a mention of "7Zip".
  const pattern = new RegExp(
    `(^|${BOUNDARY})${parts.join(SOFT_SEPARATOR)}($|${BOUNDARY})`,
    'iu',
  );
  return pattern.test(text);
}

/**
 * The one offering named in `text`, or `undefined` when none or several are.
 *
 * Ambiguity resolves only where one name contains another: "Power BI Premium"
 * beats "Power BI", because the longer name is what the user actually typed.
 * Two unrelated offerings in one question stay ambiguous and yield nothing —
 * guessing which one the reader meant is worse than not offering the download.
 */
export function findMentionedOffering<T extends OfferingLike>(
  text: string,
  offerings: readonly T[],
): T | undefined {
  const haystack = (text ?? '').trim();
  if (!haystack) {
    return undefined;
  }

  const matches = offerings.filter((offering) => {
    const name = offering.displayName?.trim();
    return !!name && name.length >= MIN_NAME_LENGTH && mentions(haystack, name);
  });

  if (matches.length <= 1) {
    return matches[0];
  }

  // Keep only the names no other match subsumes.
  const longest = matches.filter(
    (candidate) =>
      !matches.some(
        (other) =>
          other !== candidate &&
          (other.displayName ?? '').length > (candidate.displayName ?? '').length &&
          mentions(other.displayName ?? '', candidate.displayName ?? ''),
      ),
  );

  return longest.length === 1 ? longest[0] : undefined;
}

/** Display name → slug, for the offerings named in one piece of text. */
export type OfferingLinkMap = ReadonlyMap<string, string>;

/** An offering that can also be linked to, which needs a slug as well. */
export interface LinkableOffering extends OfferingLike {
  slug?: string | null;
}

/**
 * Every offering named in `text`, longest name first.
 *
 * The opposite of `findMentionedOffering` in how it treats ambiguity, because
 * the consumers want opposite things. That one drives an action, where pointing
 * at the wrong offering is worse than pointing at none. This drives links,
 * where over-matching costs a link nobody clicks — "Panorama" linking to the
 * Panorama offering is simply right.
 *
 * The order is the contract, not a detail: the renderer replaces occurrences in
 * sequence, so "Power BI Premium" must be linked before "Power BI" or the
 * shorter name would claim the first two words of the longer one.
 *
 * Offerings with no slug are dropped — there is nowhere for their link to go.
 */
export function findMentionedOfferings<T extends LinkableOffering>(
  text: string,
  offerings: readonly T[],
): T[] {
  const haystack = text?.trim();
  if (!haystack) {
    return [];
  }

  return offerings
    .filter((offering) => {
      const name = offering.displayName?.trim();
      return (
        !!name &&
        name.length >= MIN_LINKED_NAME_LENGTH &&
        !!offering.slug?.trim() &&
        mentions(haystack, name)
      );
    })
    // Non-null: the filter above only lets through records with a name.
    .sort((a, b) => b.displayName!.trim().length - a.displayName!.trim().length);
}

/**
 * The link map for one piece of text: only the offerings it actually names.
 *
 * Deliberately not the whole catalogue. The renderer runs one regex per entry
 * per line, and handing it several hundred names to test against every line of
 * every answer would be the most expensive thing on the page. A typical answer
 * names one or two.
 *
 * `Map` preserves insertion order, so the longest-name-first ordering above
 * survives into the renderer.
 */
export function offeringLinkMap<T extends LinkableOffering>(
  text: string,
  offerings: readonly T[],
): OfferingLinkMap {
  const map = new Map<string, string>();
  for (const offering of findMentionedOfferings(text, offerings)) {
    // Both are guaranteed by `findMentionedOfferings`.
    const name = offering.displayName!.trim();
    if (!map.has(name)) {
      map.set(name, offering.slug!.trim());
    }
  }
  return map;
}
